// src/pages/admin/UsagePage.tsx
import { useSearchParams } from 'react-router-dom';
import { useMemo } from 'react';
import { useMe } from '@/lib/hooks/useMe';
import { useUsageOverview } from '@/lib/hooks/admin/useUsageOverview';
import { useUsageTimeseries } from '@/lib/hooks/admin/useUsageTimeseries';
import { useUsageUsers } from '@/lib/hooks/admin/useUsageUsers';
import { useUsageTenants } from '@/lib/hooks/admin/useUsageTenants';
import { RangeSelect, presetToRange, type RangePreset } from '@/components/admin/RangeSelect';
import { TenantSelect } from '@/components/admin/TenantSelect';
import { KpiTile } from '@/components/admin/KpiTile';
import { PromptsChart } from '@/components/admin/PromptsChart';
import { UsersTable } from '@/components/admin/UsersTable';
import { TenantsTable } from '@/components/admin/TenantsTable';

type SortKey = 'lastSeenAt' | 'prompts' | 'tokens' | 'sessions' | 'conversations' | 'files';
const VALID_SORT_KEYS: readonly SortKey[] = ['lastSeenAt', 'prompts', 'tokens', 'sessions', 'conversations', 'files'];

export default function UsagePage() {
  const [params, setParams] = useSearchParams();
  const { data: me } = useMe();

  const preset = (params.get('range') as RangePreset) || '30d';
  const { from, to } = useMemo(() => presetToRange(preset), [preset]);
  const tenantIdParam = params.get('tenantId');
  // For 'admin' role, force tenantId to user's own tenant
  const isGlobal = me?.role === 'global_admin';
  const effectiveTenantId = isGlobal ? tenantIdParam : (me?.tenantId ?? null);

  const search = params.get('search') ?? '';
  const page = Number(params.get('page') ?? '1');
  const perPage = 25;
  const rawSortBy = params.get('sortBy');
  const sortBy: SortKey = VALID_SORT_KEYS.includes(rawSortBy as SortKey) ? (rawSortBy as SortKey) : 'lastSeenAt';
  const sortDir = (params.get('sortDir') as 'asc' | 'desc') || 'desc';

  // Hooks
  const overview = useUsageOverview({ from, to, tenantId: effectiveTenantId });
  const timeseries = useUsageTimeseries({ from, to, tenantId: effectiveTenantId });
  const tenantsList = useUsageTenants({ from, to }, isGlobal === true);
  const usersList = useUsageUsers(
    {
      from, to, tenantId: effectiveTenantId ?? '',
      search, page, perPage, sortBy, sortDir,
    },
    !!effectiveTenantId,
  );

  const updateParams = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v === null || v === '') next.delete(k);
      else next.set(k, v);
    }
    setParams(next);
  };

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      {(overview.isError || timeseries.isError || usersList.isError || tenantsList.isError) && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          Errore nel caricamento di alcuni dati. Verifica la connessione o aggiorna la pagina.
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold">
          Statistiche di utilizzo
          {overview.isLoading && <span className="ml-3 text-sm text-muted-foreground font-normal">Aggiornamento…</span>}
        </h1>
        <div className="flex items-center gap-3">
          {isGlobal && tenantsList.data && (
            <TenantSelect
              tenants={tenantsList.data}
              value={effectiveTenantId}
              onChange={(t) => updateParams({ tenantId: t, page: '1' })}
            />
          )}
          <RangeSelect value={preset} onChange={(p) => updateParams({ range: p, page: '1' })} />
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiTile label="Utenti attivi"  value={overview.data?.activeUsers ?? 0} />
        <KpiTile label="Prompt"         value={overview.data?.totalPrompts ?? 0} />
        <KpiTile
          label="Token"
          value={(overview.data?.totalInputTokens ?? 0) + (overview.data?.totalOutputTokens ?? 0)}
          hint={`in: ${overview.data?.totalInputTokens ?? 0} · out: ${overview.data?.totalOutputTokens ?? 0}`}
        />
        <KpiTile label="Sessioni stim." value={overview.data?.estimatedSessions ?? 0} />
        <KpiTile label="File caricati"  value={overview.data?.filesUploaded ?? 0} />
      </div>

      {/* Chart */}
      <div className="rounded-lg border bg-card p-4">
        <h2 className="text-sm font-semibold mb-3">Prompt al giorno</h2>
        <PromptsChart data={timeseries.data ?? []} />
      </div>

      {/* Table */}
      <div className="rounded-lg border bg-card p-4">
        {effectiveTenantId ? (
          <UsersTable
            rows={usersList.data?.data ?? []}
            sortBy={sortBy}
            sortDir={sortDir}
            onSort={(k) => updateParams({
              sortBy: k,
              sortDir: sortBy === k && sortDir === 'desc' ? 'asc' : 'desc',
              page: '1',
            })}
            search={search}
            onSearchChange={(v) => updateParams({ search: v, page: '1' })}
            page={page}
            perPage={perPage}
            total={usersList.data?.meta.total ?? 0}
            onPageChange={(p) => updateParams({ page: String(p) })}
          />
        ) : (
          <TenantsTable
            rows={tenantsList.data ?? []}
            onTenantClick={(t) => updateParams({ tenantId: t, page: '1' })}
          />
        )}
      </div>
    </div>
  );
}
